import React from 'react';
import { Dialog, DialogContent, IconButton, Box, Typography, Button, Alert } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import DownloadIcon from '@mui/icons-material/Download';
import WarningIcon from '@mui/icons-material/Warning';
import { PdfViewer, ImageViewer } from '../fileManager/viewers';

interface UnifiedFilePreviewProps {
  open: boolean;
  onClose: () => void;
  fileName: string;
  fileUrl: string;
  fileType?: 'pdf' | 'image' | 'video' | 'audio' | 'text' | 'unknown';
  tooLarge?: boolean;
  fileSize?: number;
  formattedSize?: string;
  onDownload?: () => void;
}

/**
 * Visualizador unificado de arquivos para toda a aplicação
 * Usado em: DocumentSlotCard, FileManager, RichCommentContent
 * Suporta: PDF, imagens, vídeo, áudio, texto
 */
export default function UnifiedFilePreview({ 
  open, 
  onClose, 
  fileName, 
  fileUrl,
  fileType,
  tooLarge = false,
  fileSize,
  formattedSize,
  onDownload
}: UnifiedFilePreviewProps) {
  if (!open || !fileUrl) return null;

  // Auto-detectar tipo se não fornecido
  const detectFileType = (): string => {
    if (fileType) return fileType;
    
    const lowerName = fileName.toLowerCase();
    if (lowerName.endsWith('.pdf')) return 'pdf';
    if (/\.(jpg|jpeg|png|gif|webp|bmp)$/i.test(lowerName)) return 'image';
    if (/\.(mp4|webm|ogg|mov)$/i.test(lowerName)) return 'video';
    if (/\.(mp3|wav|ogg|m4a)$/i.test(lowerName)) return 'audio';
    if (/\.(txt|log|md|json|xml|csv)$/i.test(lowerName)) return 'text';
    return 'unknown';
  };

  const detectedType = detectFileType();

  // Truncar nome do arquivo
  const truncateFileName = (name: string, maxLength: number = 40) => {
    if (name.length <= maxLength) return name;
    const ext = name.split('.').pop();
    const nameWithoutExt = name.substring(0, name.lastIndexOf('.'));
    const truncated = nameWithoutExt.substring(0, maxLength - ext!.length - 4);
    return `${truncated}...${ext}`;
  };

  const handleDownload = () => {
    if (onDownload) {
      onDownload();
    } else {
      const anchor = document.createElement('a');
      anchor.href = fileUrl;
      anchor.download = fileName || 'arquivo';
      document.body.appendChild(anchor);
      anchor.click();
      document.body.removeChild(anchor);
    }
  };

  const getFileExtension = (filename: string) => {
    return filename.split('.').pop()?.toLowerCase() || '';
  };

  const renderContent = () => {
    // Arquivo muito grande - mostrar aviso
    if (tooLarge) {
      return (
        <Box sx={{ p: 4, textAlign: 'center' }}>
          <WarningIcon sx={{ fontSize: 64, color: 'warning.main', mb: 2 }} />
          <Typography variant="h6" gutterBottom>
            Arquivo Muito Grande para Preview
          </Typography>
          <Alert severity="warning" sx={{ mb: 3, textAlign: 'left' }}>
            <Typography variant="body2" gutterBottom>
              <strong>Arquivo:</strong> {fileName}
            </Typography>
            <Typography variant="body2" gutterBottom>
              <strong>Tamanho:</strong> {formattedSize}
            </Typography>
            <Typography variant="body2" sx={{ mt: 2 }}>
              Este arquivo é grande demais para visualização no navegador. 
              Isso pode causar lentidão ou travamento.
            </Typography>
          </Alert>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Recomendamos fazer o download do arquivo para visualizá-lo localmente.
          </Typography>
          <Button 
            variant="contained" 
            size="large"
            startIcon={<DownloadIcon />}
            onClick={handleDownload}
          >
            Baixar Arquivo {formattedSize && `(${formattedSize})`}
          </Button>
        </Box>
      );
    }

    switch (detectedType) {
      case 'pdf':
        return (
          <Box sx={{ minHeight: 600}}>
            <PdfViewer pdfUrl={fileUrl} fileName={fileName} />
          </Box>
        );

      case 'image':
        return (
          <Box sx={{ minHeight: 500 }}>
            <ImageViewer imageUrl={fileUrl} fileName={fileName} alt={fileName} />
          </Box>
        );

      case 'video':
        return (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
            <video 
              src={fileUrl} 
              controls 
              style={{ maxWidth: '100%', maxHeight: 'calc(90vh - 200px)' }} 
            />
          </Box>
        );

      case 'audio':
        return (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <audio src={fileUrl} controls style={{ width: '100%' }} />
          </Box>
        );

      case 'text':
        return (
          <Box sx={{ minHeight: 400, p: 2 }}>
            <iframe
              src={`data:text/plain;charset=utf-8,${encodeURIComponent(fileUrl)}`}
              title="Preview"
              style={{ width: '100%', height: '70vh', border: 'none' }}
            />
          </Box>
        );

      case 'unknown':
      default:
        return (
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <Typography variant="h6" gutterBottom>
              Tipo de arquivo não suportado para visualização
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Arquivo: {fileName}
              <br />
              Extensão: {getFileExtension(fileName)}
            </Typography>
            <Button 
              variant="contained" 
              startIcon={<DownloadIcon />}
              onClick={handleDownload}
            >
              Baixar Arquivo
            </Button>
          </Box>
        );
    }
  };

  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      maxWidth="lg" 
      fullWidth
      PaperProps={{
        sx: {
          minHeight: detectedType === 'audio' ? 300 : detectedType === 'unknown' ? 400 : 600,
          maxHeight: '90vh',
        }
      }}
    >
      {/* Header compacto e sticky */}
      <Box
        sx={{
          position: 'sticky',
          top: 0,
          zIndex: 11,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          px: 2,
          py: 1,
          borderBottom: 1,
          borderColor: 'divider',
          bgcolor: 'background.paper',
        }}
      >
        <Typography 
          variant="subtitle2" 
          sx={{ 
            fontWeight: 600,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            flex: 1,
          }}
        >
          {truncateFileName(fileName)}
        </Typography>
        <IconButton onClick={onClose} size="small" sx={{ ml: 1 }}>
          <CloseIcon />
        </IconButton>
      </Box>
      <DialogContent sx={{ p: 0 }}>
        {renderContent()}
      </DialogContent>
    </Dialog>
  );
}
